import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/components/useConfirm";
import { Plus, Trash2, MapPin, Wifi, Shield } from "lucide-react";

// Plain IPv4 regex — used purely as a UX prompt; the server is the
// authoritative validator (rejects LAN/loopback/link-local/multicast).
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

function CreateSiteDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", prefix: "", code: "", static_ip: "" });
  const [busy, setBusy] = useState(false);

  const reset = () => setForm({ label: "", prefix: "", code: "", static_ip: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.prefix.trim()) {
      toast.error("Label and prefix are required");
      return;
    }
    if (form.static_ip && !IPV4_RE.test(form.static_ip.trim())) {
      toast.error("Enter a valid IPv4 address (e.g. 203.0.113.5)");
      return;
    }
    setBusy(true);
    try {
      const body = {
        label: form.label.trim(),
        prefix: form.prefix.trim().toUpperCase(),
        static_ip: form.static_ip.trim() || null,
      };
      if (form.code.trim()) body.code = form.code.trim().toUpperCase();
      await api.post("/admin/sites", body);
      toast.success(`Site "${form.label}" created`);
      reset();
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create site");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <DialogTrigger asChild>
        <Button data-testid="settings-add-site-btn" className="bg-[#1A2744] hover:bg-[#0F1A30]">
          <Plus className="h-4 w-4 mr-2" /> Add site
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add a new site</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Site label</Label>
            <Input
              data-testid="site-form-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Auckland"
            />
            <p className="text-xs text-gray-500 mt-1">Human-friendly name shown in dropdowns + reports.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Employee-code prefix</Label>
              <Input
                data-testid="site-form-prefix"
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })}
                placeholder="AKL"
                maxLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">2–6 letters. New employees auto-numbered AKL001 …</p>
            </div>
            <div>
              <Label>Internal code (optional)</Label>
              <Input
                data-testid="site-form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="auto"
              />
              <p className="text-xs text-gray-500 mt-1">Defaults to <code>SITE_&lt;PREFIX&gt;</code>.</p>
            </div>
          </div>
          <div>
            <Label>Cafe Wi-Fi static IP (optional)</Label>
            <Input
              data-testid="site-form-static-ip"
              value={form.static_ip}
              onChange={(e) => setForm({ ...form, static_ip: e.target.value })}
              placeholder="203.0.113.5"
              inputMode="decimal"
              autoComplete="off"
            />
            <p className="text-xs text-gray-500 mt-1">
              The cafe's <strong>public WAN IPv4</strong>. One IP per site.
              Discover it from a phone on the cafe Wi-Fi by opening <code>/api/ip-check</code>.
              Leave blank to keep clock-in in warn-only mode.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              data-testid="site-form-submit"
              disabled={busy}
              className="bg-[#1A2744] hover:bg-[#0F1A30]"
            >
              {busy ? "Creating…" : "Create site"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StaticIpEditor({ site, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setValue(site.static_ip || "");
  }, [open, site]);

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed && !IPV4_RE.test(trimmed)) {
      toast.error("Enter a valid IPv4 address (e.g. 203.0.113.5) or leave blank to clear");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/sites/${site.code}/cidrs`, { static_ip: trimmed });
      toast.success(trimmed ? `Wi-Fi static IP set to ${trimmed}` : "Wi-Fi static IP cleared (warn-only mode)");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        data-testid={`site-static-ip-edit-${site.code}`}
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
      >
        <Wifi className="h-3.5 w-3.5 mr-1" /> Wi-Fi IP
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Cafe Wi-Fi · {site.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Public WAN IPv4</Label>
            <Input
              data-testid="site-static-ip-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="203.0.113.5"
              inputMode="decimal"
              autoComplete="off"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
            />
            <p className="text-[11px] text-gray-500 mt-2">
              Clock-in &amp; clock-out are locked to this IP. Only the cafe's
              public WAN IPv4 is accepted — LAN ranges (10.x, 172.16–31.x,
              192.168.x) are rejected because they can never be a real public IP.
              From a phone on the cafe Wi-Fi, open{" "}
              <code className="text-[10px] bg-gray-100 px-1 rounded">/api/ip-check</code>{" "}
              and copy the <code>ip</code> field.
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              Leave blank to clear and run the gate in <strong>warn-only</strong> mode.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="site-static-ip-save"
            disabled={busy}
            onClick={save}
            className="bg-[#1A2744] hover:bg-[#0F1A30]"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SitesSettings() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sites");
      setSites(data.sites || []);
    } catch {
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (site, active) => {
    try {
      await api.patch(`/admin/sites/${site.code}`, { is_active: active });
      toast.success(active ? "Site reactivated" : "Site archived");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (site) => {
    const ok = await confirm({
      title: "Delete site?",
      description: `Permanently remove "${site.label}". This is only possible if no employee is assigned.`,
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/sites/${site.code}`);
      toast.success("Site deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#1A2744] flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#C9A84C]" /> Sites &amp; Wi-Fi gates
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Add new locations and set the single static <strong>public WAN IPv4</strong>{" "}
            that gates clock-in &amp; clock-out for each cafe.
          </p>
        </div>
        <CreateSiteDialog onSaved={load} />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>Code · Prefix</TableHead>
                <TableHead>Cafe Wi-Fi IP</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">Loading…</TableCell></TableRow>}
              {!loading && sites.map((s) => (
                <TableRow key={s.code} data-testid={`site-row-${s.code}`}>
                  <TableCell>
                    <div className="font-medium text-[#1A2744]">{s.label}</div>
                    {s.is_protected && <div className="text-[10px] text-gray-400 mt-0.5">Protected · cannot be deleted</div>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{s.code}</div>
                    <div className="text-gray-400">{s.prefix}###</div>
                  </TableCell>
                  <TableCell>
                    {s.static_ip ? (
                      <Badge variant="outline" className="font-mono text-xs" data-testid={`site-static-ip-${s.code}`}>
                        {s.static_ip}
                      </Badge>
                    ) : (
                      <span className="text-xs text-amber-600" data-testid={`site-static-ip-${s.code}-none`}>
                        ⚠ none — clock-in is warn-only
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`site-toggle-${s.code}`}
                        checked={!!s.is_active}
                        onCheckedChange={(v) => toggleActive(s, v)}
                        disabled={s.is_protected && s.is_active}
                      />
                      <span className="text-xs text-gray-600">{s.is_active ? "Active" : "Archived"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <StaticIpEditor site={s} onSaved={load} />
                    {!s.is_protected && (
                      <Button
                        data-testid={`site-delete-${s.code}`}
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => remove(s)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 flex gap-2">
        <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Heads up · Tier-C clock-in gate</p>
          <p>
            Clock-in / clock-out is restricted to each site's single static
            WAN IP. While no IP is configured for an active site, the gate
            runs in <strong>warn-only</strong> mode (logged, not blocked) so
            you can roll out gradually.
          </p>
        </div>
      </div>
    </div>
  );
}
